import { AnalyticsBrowser } from '@segment/analytics-next'
import mixpanel from 'mixpanel-browser'
import React, { useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'
import {
  AnalyticsContext,
  ExtendAnalyticsBrowser,
} from 'src/analytics/AnalyticsContext'
import useConfig from 'src/context/ConfigContext/useConfig'
import { selectIsCspAuthenticatedFromState } from 'src/redux/selectors/auth.selectors'
import { selectFeatureFlaggerFromState } from 'src/redux/selectors/data/featureFlagger.selectors'

interface AnalyticsProviderProps {
  children: React.ReactNode
}

export const AnalyticsProvider: React.FC<AnalyticsProviderProps> = ({
  children,
}) => {
  const enableMixpanelAnalytics = useSelector(
    selectFeatureFlaggerFromState,
  )?.enable_mixpanel_analytics
  const enableAnalyticsLogging = useSelector(
    selectFeatureFlaggerFromState,
  )?.enable_analytics_logging
  const isCspAuthenticated = useSelector(selectIsCspAuthenticatedFromState)
  const { brand: brandConfig, environment: envConfig } = useConfig()

  const segmentInstance = useMemo(() => {
    return AnalyticsBrowser.load(
      {
        writeKey: brandConfig.SEGMENT_KEY ?? '',
        cdnURL: 'https://sdks.paywithextend.com',
      },
      {
        integrations: {
          'Segment.io': {
            apiHost: 'sa.paywithextend.com/v1',
            protocol: 'https', // optional
          },
        },
      },
    )
  }, [brandConfig])

  const mixpanelInstance = useMemo(() => {
    return mixpanel.init(envConfig.MIXPANEL_TOKEN ?? '', {
      debug: !!enableAnalyticsLogging,
      ignore_dnt: true,
      persistence: 'cookie',
      api_host: 'https://mx.paywithextend.com',
    })
  }, [envConfig])

  const analyticsContext = useMemo(
    () => ({
      analytics: new ExtendAnalyticsBrowser(segmentInstance, mixpanelInstance),
    }),
    [segmentInstance, mixpanelInstance],
  )

  useEffect(() => {
    analyticsContext.analytics.loggingEnabled = !!enableAnalyticsLogging
    analyticsContext.analytics.mixpanelIsEnabled = enableMixpanelAnalytics
    analyticsContext.analytics.isCspAuthenticated = isCspAuthenticated
  }, [
    analyticsContext,
    enableAnalyticsLogging,
    isCspAuthenticated,
    enableMixpanelAnalytics,
  ])

  return (
    <AnalyticsContext.Provider value={analyticsContext}>
      {children}
    </AnalyticsContext.Provider>
  )
}
